<template>
  <span
    class="c-universal-search-menu ph-no-capture dd-privacy-mask"
    role="presentation"
    @keyup.down="onDownPressed"
    @keyup.up="onUpPressed"
  >
    <BittsInput
      v-if="expanded || !mobile"
      v-model="searchTerm"
      ref="searchInputRef"
      placeholder="Search for anything"
      name="universal_search"
      input-classes="text-neutral-800"
      class="universal-search"
      type="search"
      :focus="isSearchFocused"
      @blur="handleBlur"
      @focus="onSearchFocus"
      @enter-pressed="onEnterPressed"
    >
      <template #suffix>
        <div
          v-if="!isSearchFocused && !searchTerm"
          class="c-bitts-input__shortcut-label"
          role="presentation"
          @click="handlePrefixClick"
        >
          <FontAwesomeIcon
            :icon="['fas', 'command']"
            class="text-sm"
            v-if="ctrlOrMeta() === '⌘'"
          />
          <span v-else>ctrl</span>
          /
        </div>
        <span v-else class="h-[24px]" />
      </template>
    </BittsInput>
    <div
      v-if="showDropdown"
      ref="dropdownRef"
      class="c-universal-search-menu__content"
    >
      <div v-if="loading" class="c-universal-search-results--loading">
        <img
          class="c-universal-search-results__searching-icon"
          src="/images/gifs/loading.gif"
          alt=""
        />
        Searching...
      </div>
      <ul
        v-else
        class="c-dropdown-menu__list c-universal-search-results ph-no-capture dd-privacy-mask"
      >
        <div
          v-for="(searchResult, index) in searchResults"
          :key="`search_result_${index}`"
          :class="{
            'bg-neutral-background-weak':
              isUserHovering && currentIndex === index,
          }"
          @mouse-enter="onItemHighlighted(index)"
          @mouse-leave="isUserHovering = false"
        >
          <li
            v-if="typeTitle(index)"
            class="c-universal-search-results__section-title"
            >{{ typeTitle(index) }}</li
          >
          <UniversalSearchResult
            ref="searchResultRefs"
            :search-result="searchResult as SearchResult"
            :search-term="searchTerm"
            @on-search-result-selected="closeAndReset"
          />
        </div>
        <div
          v-if="!loading && searchResults.length === 0"
          class="c-universal-search-results--no-results"
        >
          No results found for "{{ searchTerm }}"
        </div>
        <button
          v-if="hasPermission('manage:partnerships')"
          class="self-start"
          :is-active="currentIndex === searchResults.length && isUserHovering"
          @mouse-enter="onItemHighlighted(searchResults.length)"
          @mouse-leave="isUserHovering = false"
          type="button"
        >
          <div class="c-universal-search-results__section-title text-left">
            Invite Partner
          </div>
          <UniversalSearchResult
            ref="invitePartner"
            :search-term="searchTerm"
            @on-search-result-selected="closeAndReset"
          />
        </button>
      </ul>
    </div>
  </span>
</template>

<script setup lang="ts">
import { BittsInput } from '@crossbeam/bitts';

import { onClickOutside } from '@vueuse/core';
import {
  DebouncedFunc,
  debounce,
  get as lodashGet,
  set as lodashSet,
} from 'lodash';
import { onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';

import UniversalSearchResult from '@/components/navbar/UniversalSearchResult.vue';

import { crossbeamApi } from '@/api';
import { useAbortableRequest } from '@/composables/useAbortableRequest';
import useAuth from '@/composables/useAuth';
import { COMPANY, PERSON, RESULT_TYPES } from '@/constants/universal_search';
import { captureException } from '@/errors';
import {
  useFeedsStore,
  useFlashesStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import type {
  ApiSearchResponse,
  SearchData,
  SearchDataItem,
  SearchResult,
} from '@/types/search';
import { ctrlOrMeta, sortByKey } from '@/utils';

interface Props {
  expanded: boolean;
  mobile?: boolean;
  toggleSearchExpansion?: () => void;
  sidebar?: boolean;
}

const {
  expanded,
  mobile = false,
  toggleSearchExpansion = undefined,
  sidebar = false,
} = defineProps<Props>();
const emit = defineEmits<(e: 'item-selected') => void>();
const sourcesStore = useSourcesStore();
const flashesStore = useFlashesStore();
const populationsStore = usePopulationsStore();
const feedsStore = useFeedsStore();
const { hasPermission } = useAuth();

const showDropdown = ref(false);
const searchTerm = ref('');
const loading = ref(false);
const searchDebounced = ref<DebouncedFunc<
  (searchTerm: string) => Promise<void>
> | null>(null);
const searchResults = ref<SearchResult[]>([]);
const currentIndex = ref(-1);
const isSearchFocused = ref(false);
const isUserHovering = ref(false);

const searchResultRefs = ref<InstanceType<typeof UniversalSearchResult>[]>([]);
const invitePartner = ref<InstanceType<typeof UniversalSearchResult> | null>(
  null,
);
const dropdownRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<InstanceType<typeof BittsInput> | null>(null);

let lodashPath: string | undefined;
let query: SearchResult;

const { getSignal } = useAbortableRequest();

onClickOutside(
  dropdownRef,
  () => {
    if (!isSearchFocused.value) {
      // This indicates the user has clicked away from the dropdown box, but
      // they've actually clicked into the search bar, in which case we don't
      // actually want to close the dropdown.
      closeUniversalSearch();
    }
  },
  {
    // Tooltip CTA and replace dialog are teleported to body. Closing the
    // dropdown on those clicks unmounts UniversalSearchResult and destroys
    // the dialog before it can stay open.
    ignore: ['.bitts-tooltip', '.o-bitts-modal', 'dialog'],
  },
);

watch(
  () => searchTerm.value,
  (searchTerm) => {
    if (!searchTerm && !sidebar) {
      showDropdown.value = false;
      currentIndex.value = -1;
      searchResults.value = [];
      return;
    }
    onSearch();
  },
);

onBeforeMount(async () => {
  searchDebounced.value = debounce(performSearch, 300);
  const vuexPromises = [sourcesStore.readySync];
  const promises = searchTerm.value
    ? vuexPromises.concat(performSearch(searchTerm.value))
    : vuexPromises;
  await Promise.all(promises);

  window.addEventListener('keydown', handleKeydown);
});

function onSearch() {
  loading.value = true;
  showDropdown.value = true;
  searchDebounced.value?.(searchTerm.value || ' ');
}

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
});

function typeTitle(idx: number) {
  const current = searchResults.value[idx]?.type;
  const previous = searchResults.value[idx - 1]?.type;
  if (!previous || previous !== current)
    return transformTypeText(current || '');
  return null;
}
function transformTypeText(type: string) {
  const types: Record<string, string> = {
    partner_orgs: 'Your Partners',
    populations: 'Populations',
    records: 'Records',
    suggested: 'Suggested Partners',
  };
  return types[type];
}
function closeUniversalSearch() {
  showDropdown.value = false;
  isSearchFocused.value = false;
}
function closeAndReset() {
  isUserHovering.value = false;
  isSearchFocused.value = false;
  searchTerm.value = '';
  searchResults.value = [];
  currentIndex.value = -1;
  closeUniversalSearch();
  emit('item-selected');
}
function onSearchFocus() {
  showDropdown.value = searchTerm.value !== '';
  isSearchFocused.value = true;
}
function onUpPressed() {
  isUserHovering.value = true;
  if (!showDropdown.value) return;
  currentIndex.value = Math.max(currentIndex.value - 1, 0);
}
function onDownPressed() {
  isUserHovering.value = true;
  if (!showDropdown.value) return;
  currentIndex.value = Math.min(
    currentIndex.value + 1,
    searchResults.value.length,
  );
}
function onEnterPressed() {
  if (!showDropdown.value) return;
  if (searchResults.value.length === 0 && currentIndex.value !== 0) return;

  isSearchFocused.value = false;
  if (currentIndex.value === -1) {
    onItemSelected(lodashSet({}, lodashPath || '', query) as SearchResult);
  } else if (currentIndex.value === searchResults.value.length) {
    invitePartner.value?.$el?.click();
  } else {
    searchResultRefs.value[currentIndex.value]?.$el?.click();
  }
  closeAndReset();
}
function onItemHighlighted(index: number) {
  isUserHovering.value = true;
  currentIndex.value = index;
}
function onItemSelected(searchResult: SearchResult) {
  if (lodashPath) {
    query = lodashGet(searchResult, lodashPath);
  } else {
    query = searchResult;
  }
}
async function searchCrossbeamCompanies(query: string, signal?: AbortSignal) {
  const { data, error } = await crossbeamApi.GET(
    '/v0.1/discoverable-org-search',
    {
      params: {
        query: { query },
      },
      signal,
    },
  );
  if (signal?.aborted) return;
  if (error) {
    captureException(error);
    flashesStore.addErrorFlash({ message: error });
    return;
  }
  return data.items;
}
async function performSearch(searchTerm: string) {
  const signal = getSignal();
  loading.value = true;
  const params = { params: { query: { search: searchTerm } }, signal };
  try {
    const [universalSearchResults, suggestedCrossbeam] = await Promise.all([
      crossbeamApi.GET('/v0.1/search', params),
      searchCrossbeamCompanies(searchTerm, signal),
      sourcesStore.readySync,
      populationsStore.readySync,
    ]);

    if (signal?.aborted) return;

    const apiData = universalSearchResults.data as ApiSearchResponse;
    const results: SearchData = {
      companies: apiData.companies || [],
      people: apiData.people || [],
      populations: apiData.populations || [],
      partner_orgs: apiData.partner_orgs || [],
      suggested: suggestedCrossbeam,
    };
    searchResults.value = transform(results) as SearchResult[];
  } catch (error) {
    if (!signal?.aborted) throw error;
  } finally {
    if (!signal?.aborted) loading.value = false;
  }
}
function transform(data: SearchData) {
  const addType = (item: SearchDataItem, type: string) => {
    return {
      type: type === COMPANY || type === PERSON ? 'records' : type,
      subType: type === COMPANY || type === PERSON ? type : null,
      ...item,
    };
  };
  const addSource = (item: SearchDataItem) => {
    if (item.source_id) {
      const source = sourcesStore.getSourceById(item.source_id);
      if (source) {
        return {
          source_name: source.table,
          integration: source.feed_id
            ? feedsStore.getFeedById(source.feed_id)?.integration || ''
            : '',
          ...item,
        };
      }
    }
    return item;
  };
  const addPopulations = (item: SearchDataItem) => {
    if (!item.population_ids) return item;
    return {
      ...item,
      populations: item.population_ids.map(populationsStore.getPopulationById),
    };
  };
  const removeDeadResults = (item: SearchDataItem) => {
    if (item.subType && [COMPANY, PERSON].includes(item.subType)) {
      return item.source_id
        ? !!sourcesStore.getSourceById(item.source_id)
        : false;
    }
    return true;
  };

  const sortedData: SearchData = {
    ...data,
    [COMPANY]:
      data[COMPANY]?.sort(sortByKey('population_ids', 'desc', 'length')) || [],
    [PERSON]:
      data[PERSON]?.sort(sortByKey('population_ids', 'desc', 'length')) || [],
  };

  return RESULT_TYPES.filter((type) => sortedData[type as keyof SearchData])
    .map((type) =>
      (sortedData[type as keyof SearchData] || [])
        .map((item) => addType(item, type))
        .filter(removeDeadResults)
        .map(addSource)
        .map(addPopulations)
        .slice(0, 5),
    )
    .flat();
}
function handlePrefixClick() {
  if (mobile && expanded && toggleSearchExpansion) {
    toggleSearchExpansion();
  }
}
function handleKeydown(e: KeyboardEvent) {
  /* Linux + Windows use Ctrl Key, MacOS uses ⌘, see:
   * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/metaKey */

  if ((ctrlOrMeta() === 'ctrl' ? e.ctrlKey : e.metaKey) && e.key === '/') {
    const inputEl = document.querySelector(
      '.universal-search .c-bitts-input__input input',
    );
    if (inputEl instanceof HTMLInputElement) {
      inputEl.focus();
      isSearchFocused.value = true;
    }
  }
}
function handleBlur() {
  isSearchFocused.value = false;
}

function focusSearchInput() {
  searchInputRef.value?.focusInput();
}

defineExpose({
  focusSearchInput,
  onSearch,
});
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-dropdown-menu__list {
  @apply list-none p-0;
}

.c-universal-search-menu {
  @apply relative w-full rounded-8 shadow-component;
}

.c-universal-search-menu__content {
  @apply ml-0 border border-solid border-neutral-border absolute mt-4 bg-white overflow-auto rounded-8 w-full shadow-overlay;
  z-index: 2;
}

.c-universal-search-menu__content--expanded {
  @apply w-full;
}

.c-universal-search-results {
  @apply px-4 pt-8 pb-12;
}

.c-universal-search-results--loading {
  @apply flex items-center text-center flex-col justify-center text-base;
  flex: 1;
  min-height: 77px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.c-universal-search-results--no-results {
  @apply text-neutral-400 flex flex-col items-center leading-4;
}

.c-universal-search-results__item {
  @apply text-base flex justify-between items-center py-4 px-8 relative;
  white-space: nowrap;
}

.c-universal-search-results__searching-icon {
  width: 32px;
}

.c-universal-search-results__section-title {
  @apply text-neutral-500 pb-4 mt-8 pl-8 text-sm;
}

.c-dropdown-menu__list__item:first-child
  .c-universal-search-results__section-title {
  @apply mt-0;
}
</style>
<style lang="pcss">
@reference "../../css/theme.css";
.c-universal-search-menu {
  .c-dropdown-menu-list__item {
    @apply text-left w-full;
  }

  .c-dropdown-menu-list__item--active .c-universal-search-results__item {
    @apply bg-neutral-100;
  }

  .ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover {
    border-right-width: 1px !important;
  }

  .c-bitts-input {
    .c-bitts-input__shortcut-label {
      @apply text-neutral-accent border rounded-md border-neutral-border px-6 flex items-center gap-4;
    }
  }
}
</style>
